import Fill from 'ol/style/Fill';
import Polygon from 'ol/geom/Polygon';
import {calculateExtent, hexToRgbA} from '/src/modules/misc/misc';
import Stroke from 'ol/style/Stroke';
import {defineFeatureStyle, dividerInt, stringDivider} from '/src/modules/features/featureStyle';
import {getCenter as getExtentCenter, getHeight as getExtentHeight, getTopLeft, getWidth as getExtentWidth} from 'ol/extent';
import {replaceTextPlaceholder} from '/src/modules/language/language';
import Point from 'ol/geom/Point';
import Text from 'ol/style/Text';
import Style from 'ol/style/Style';

/**
 * Function for styling a lineString
 * @param {object} feature
 * @param {number} resolution
 * @return {*}
 */
export function styleLineString(feature, resolution) {
  let text;
  let style;
  let textObject;
  let numberObject;
  let textStyle;
  let fontSize = 12;

  const featureStyle = defineFeatureStyle(feature);
  const featureMainExhibitor = feature.get('mainExhibitor');
  const featureNumber = typeof feature.get('number') !== 'undefined' ? feature.get('number') : null;

  if (typeof feature.get('name') !== 'undefined') {
    let featureName = feature.get('name');

    featureName = replaceTextPlaceholder(featureName);

    text = stringDivider(featureName, dividerInt, '\n');
  } else {
    text = '';
  }

  style = new Style({
    stroke: new Stroke({
      color: featureStyle['general']['stroke'] !== null ? featureStyle['general']['stroke'] : '#000000',
      width: featureStyle['general']['strokeWidth'] !== null ? featureStyle['general']['strokeWidth'] : 1,
      opacity: featureStyle['general']['strokeOpacity'] !== null ? featureStyle['general']['strokeOpacity'] : 0,
    }),
  });

  if (featureStyle['text'] !== null) {
    if (featureStyle['text']['maxResolution'] > resolution) {
      let numberInCenter = false;
      if (feature.get('type') === 'Stand') {
        if (typeof featureMainExhibitor !== 'undefined' && featureMainExhibitor.length > 0) {
          if (featureMainExhibitor[0]['name'] !== null) {
            text = stringDivider(featureMainExhibitor[0]['name'], dividerInt, '\n');
          } else {
            if (typeof featureNumber !== 'undefined' && featureNumber !== null && featureNumber !== '') {
              text = ' ' + featureNumber;
              numberInCenter = true;
            } else {
              text = '';
            }
          }
        } else {
          if (typeof featureNumber !== 'undefined' && featureNumber !== null && featureNumber !== '') {
            text = ' ' + featureNumber;
            numberInCenter = true;
          } else {
            text = '';
          }
        }

        textObject = new Text({
          font: fontSize + 'px sans-serif',
          fill: new Fill({
            color: featureStyle['text']['color'],
          }),
          text: text,
          overflow: false,
          rotateWithView: true,
        });

        /**
         * If the text needs to rotate, calculate a new pseudo feature to match the extent for the rotation.
         */
        if (getExtentWidth(feature.getGeometry().getExtent()) < getExtentHeight(feature.getGeometry().getExtent())) {
          textObject.setRotation(90 * (Math.PI / 180));
          textStyle = new Style({
            geometry: function(feature) {
              let pseudoPolygon = new Polygon([calculateExtent(feature.getGeometry().getCoordinates()[0])]);
              pseudoPolygon.rotate(90 * (Math.PI / 180), getExtentCenter(feature.getGeometry().getExtent()));
              return pseudoPolygon;
            },
          });
          textStyle.setText(textObject);
        } else {
          style.setText(textObject);
        }

        if (featureStyle['text'] !== null && featureStyle['text']['stroke'] !== null) {
          style.setStroke(new Stroke({
            color: featureStyle['text']['stroke'],
            width: featureStyle['text']['strokeWidth'],
            opacity: featureStyle['text']['strokeOpacity'],
          }));
        }

        if (numberInCenter === false) {
          if (typeof feature.get('number') !== 'undefined') {
            numberObject = new Style({
              text: new Text({
                text: featureNumber !== null ? ' ' + featureNumber : '',
                font: fontSize - 1 + 'px sans-serif',
                fill: new Fill({
                  color: featureStyle['number']['color'],
                }),
                placement: 'point',
                textAlign: 'end',
                rotateWithView: true,
                overflow: false,
                offsetY: 8.5,
                offsetX: 8.5,
              }),
              geometry: function(feature) {
                return new Point(getTopLeft(feature.getGeometry().getExtent()));
              },
            });

            if (featureStyle['number']['backgroundFill'] !== null) {
              numberObject.setBackgroundFill(new Fill({
                color: featureStyle['number']['backgroundFill'],
              }));
            }
          }
        }
      } else {
        textObject = new Text({
          font: fontSize + 'px sans-serif',
          fill: new Fill({
            color: featureStyle['text']['color'],
          }),
          text: text,
          overflow: false,
          rotateWithView: false,
        });

        if (featureStyle['text'] !== null && featureStyle['text']['stroke'] !== null) {
          textObject.setStroke(new Stroke({
            color: featureStyle['text']['stroke'],
            width: featureStyle['text']['strokeWidth'],
            opacity: featureStyle['text']['strokeOpacity'],
          }));
        }
      }

      // style.setText(textObject);
    }
  }

  let spartenkanalNumberObject;

  if (typeof feature.get('spartenkanalNummer') !== 'undefined') {
    if (resolution < 0.4) {
      spartenkanalNumberObject = new Style({
        text: new Text({
          text: feature.get('spartenkanalNummer') !== null ? ' ' + feature.get('spartenkanalNummer') : '',
          font: fontSize - 1 + 'px sans-serif',
          fill: new Fill({
            color: '#000000',
          }),
          placement: 'point',
          textAlign: 'center',
          rotation: typeof feature.get('align') !== 'undefined' ? (feature.get('align') === 'left' ? 90 * (Math.PI / 180) : 90 * (-Math.PI / 180)) : 0,
          rotateWithView: true,
          overflow: true,
          offsetY: 8.5,
          textBaseline: 'top',
        }),
        geometry: function(feature) {
          if (typeof feature.get('align') !== 'undefined') {
            let coordinates = feature.getGeometry().getCoordinates(false);
            if (feature.get('align') === 'left') {
              if (coordinates[0][0] < coordinates[1][0]) {
                return new Point(feature.getGeometry().getCoordinates(false)[0]);
              } else {
                return new Point(feature.getGeometry().getCoordinates(false)[1]);
              }
            } else {
              if (coordinates[0][0] < coordinates[1][0]) {
                return new Point(feature.getGeometry().getCoordinates(false)[1]);
              } else {
                return new Point(feature.getGeometry().getCoordinates(false)[0]);
              }
            }
          }
        },
      });
    }
  }

  style.setFill(new Fill({
    color: hexToRgbA(featureStyle['general']['fill'], featureStyle['general']['fillOpacity']),
  }));

  if (feature.get('type') === 'Stand') {
    if (resolution > 0.4) {
      style.getFill().setColor(hexToRgbA(featureStyle['general']['fill'], 0.5));
    } else {
      style.getFill().setColor(hexToRgbA(featureStyle['general']['fill'], featureStyle['general']['fillOpacity']));
    }
  }

  if (typeof numberObject !== 'undefined' && numberObject !== null && typeof textStyle !== 'undefined' && textStyle !== null) {
    return [style, textStyle, numberObject];
  } else if (typeof textStyle !== 'undefined' && textStyle !== null) {
    return [style, textStyle];
  } else if (typeof numberObject !== 'undefined' && numberObject !== null) {
    return [style, numberObject];
  } else if (typeof spartenkanalNumberObject !== 'undefined') {
    return [style, spartenkanalNumberObject];
  } else {
    return style;
  }
}
